import { HierarcicalObjectReference } from "@novorender/data-js-api";
import pMap from "p-map";

import { AppObjectDB } from "utils/appObjectDb";
import { preloadDescendantsFastPath } from "utils/search";

export const MAX_PRELOAD_CONCURRENCY = 5;

/**
 * Warms the `descendants` cache for the objects using only the cheap
 * pre-computed file ({@link preloadDescendantsFastPath}). This feeds the cache
 * that {@link getGroupStateForIdSet} relies on to propagate selection /
 * visibility state up to folder nodes.
 *
 * Best-effort and deliberately cheap: nodes without a pre-computed descendants
 * file are skipped rather than falling back to an unbounded subtree search, so
 * background warm-ups can't flood the backend with paginated `search` requests.
 * The (expensive) search fallback stays reserved for deliberate user actions.
 *
 * @returns `true` when at least one object was populated and the batch was not aborted
 */
export async function fetchDescendantsBatch(
    db: AppObjectDB,
    objects: HierarcicalObjectReference[],
    signal: AbortSignal,
): Promise<boolean> {
    let populated = false;
    try {
        await pMap(
            objects,
            async (obj) => {
                if (await preloadDescendantsFastPath({ db, parentNode: obj, abortSignal: signal })) {
                    populated = true;
                }
            },
            { concurrency: MAX_PRELOAD_CONCURRENCY, signal },
        );
    } catch {
        // pMap rejects when the signal aborts; treat as "nothing to show".
        return false;
    }
    return populated && !signal.aborted;
}
